import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { FileCheck, Loader2, X, MessageSquare, CheckCircle2 } from "lucide-react";
import { relatoriosAPI } from "../../../services/relatorios";
import { Relatorio, RelatorioComentario } from "../../../types/relatorios";
import { Badge } from "../../ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface VisualizarRelatorioModalProps {
  isOpen: boolean;
  onClose: () => void;
  idGrupo: string;
  relatorio: Relatorio;
  canConfirm?: boolean;
  onSuccess?: () => void;
}

export function VisualizarRelatorioModal({
  isOpen,
  onClose,
  idGrupo,
  relatorio: initialRelatorio,
  canConfirm = false,
  onSuccess
}: VisualizarRelatorioModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadingComentarios, setLoadingComentarios] = useState(true);
  const [relatorio, setRelatorio] = useState<Relatorio>(initialRelatorio);
  const [comentarios, setComentarios] = useState<RelatorioComentario[]>([]);
  const [novoComentario, setNovoComentario] = useState("");
  const [adicionandoComentario, setAdicionandoComentario] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRelatorio(initialRelatorio);
      loadComentarios();
    }
  }, [isOpen, initialRelatorio]);

  const loadComentarios = async () => {
    try {
      setLoadingComentarios(true);
      const response = await relatoriosAPI.buscarPorId(initialRelatorio.id, true);
      setComentarios(response.comentarios || []);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar comentários", {
        variant: "error",
      });
    } finally {
      setLoadingComentarios(false);
    }
  };

  const handleAdicionarComentario = async () => {
    if (!novoComentario.trim()) {
      enqueueSnackbar("Digite um comentário", { variant: "warning" });
      return;
    }

    try {
      setAdicionandoComentario(true);
      await relatoriosAPI.adicionarComentario(relatorio.id, novoComentario);
      enqueueSnackbar("Comentário adicionado com sucesso!", { variant: "success" });
      setNovoComentario("");
      await loadComentarios();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao adicionar comentário", {
        variant: "error",
      });
    } finally {
      setAdicionandoComentario(false);
    }
  };

  const handleConfirmar = async () => {
    if (!window.confirm("Deseja realmente confirmar este relatório?")) {
      return;
    }

    try {
      setLoading(true);
      await relatoriosAPI.confirmar(relatorio.id);
      enqueueSnackbar("Relatório confirmado com sucesso!", { variant: "success" });
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao confirmar relatório", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNovoComentario("");
    onClose();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'rascunho':
        return <Badge variant="secondary" className="bg-gray-500">Rascunho</Badge>;
      case 'enviado':
        return <Badge variant="secondary" className="bg-blue-500">Enviado</Badge>;
      case 'confirmado':
        return <Badge variant="secondary" className="bg-green-500">Confirmado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {relatorio.titulo}
              {getStatusBadge(relatorio.status)}
            </CardTitle>
            <CardDescription>
              Por {relatorio.militar?.nick || 'Desconhecido'} em{' '}
              {format(new Date(relatorio.criado_em), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto space-y-6">
          {/* Informações do Relatório */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Categoria</Label>
                <div className="flex items-center gap-2 mt-1">
                  {relatorio.categoria && (
                    <span className="font-medium capitalize">{relatorio.categoria}</span>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Turma</Label>
                <p className="mt-1 font-medium">{relatorio.turma?.nome || 'N/A'}</p>
              </div>
            </div>

            {relatorio.status === 'confirmado' && relatorio.confirmadoPor && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Confirmado por {relatorio.confirmadoPor.nick} em{' '}
                    {relatorio.data_confirmacao && format(new Date(relatorio.data_confirmacao), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Conteúdo */}
          <div>
            <Label className="text-muted-foreground">Conteúdo</Label>
            <div
              className="mt-2 p-4 border border-border rounded-md bg-muted prose prose-sm dark:prose-invert max-w-none [&_*]:text-foreground"
              dangerouslySetInnerHTML={{ __html: relatorio.conteudo }}
            />
          </div>

          {/* Comentários */}
          <div className="border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              <Label className="text-lg font-semibold">Comentários ({comentarios.length})</Label>
            </div>

            {loadingComentarios ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[oklch(0.45_0.15_142)]" />
              </div>
            ) : (
              <div className="space-y-3">
                {comentarios.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Nenhum comentário ainda</p>
                ) : (
                  comentarios.map((comentario) => (
                    <div key={comentario.id} className="p-3 bg-muted rounded-md border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-foreground">
                          {comentario.militar?.nick || 'Desconhecido'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(comentario.criado_em), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap text-foreground">{comentario.comentario}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Adicionar Comentário (apenas para admins/responsáveis) */}
            {canConfirm && (
              <div className="mt-4">
                <textarea
                  value={novoComentario}
                  onChange={(e) => setNovoComentario(e.target.value)}
                  placeholder="Adicionar um comentário..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none"
                  rows={3}
                  disabled={adicionandoComentario}
                />
                <Button
                  onClick={handleAdicionarComentario}
                  disabled={adicionandoComentario || !novoComentario.trim()}
                  className="mt-2 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  size="sm"
                >
                  {adicionandoComentario ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Adicionar Comentário
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={loading}
            >
              Fechar
            </Button>
            {canConfirm && relatorio.status === 'enviado' && (
              <Button
                onClick={handleConfirmar}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirmar Relatório
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
